import { applyUserVoicePrefs } from '@/lib/voicePreferences';

export type AudioProcessingMode = 'voice' | 'high' | 'studio';

/** Map an Opus bitrate (kbps) to the processing tier that drives codec
 *  params + capture fidelity. 192+ = studio (stereo, DTX off), 128+ = high,
 *  else voice. */
export function resolveAudioProcessingMode(kbps: number): AudioProcessingMode {
  if (kbps >= 192) return 'studio';
  if (kbps >= 128) return 'high';
  return 'voice';
}

/**
 * Rewrite the Opus `a=fmtp` line of an SDP description to pin our bitrate
 * and codec params (FEC always on; DTX off + stereo only in studio mode).
 * Pure: returns a new description; if there's no Opus rtpmap line it returns
 * the input untouched.
 */
export function enhanceOpusDescription(
  description: RTCSessionDescriptionInit,
  audioBitrateKbps: number,
  audioProcessingMode: AudioProcessingMode,
): RTCSessionDescriptionInit {
  if (!description.sdp) return description;
  const opusPayloadType = description.sdp.match(/^a=rtpmap:(\d+) opus\/48000(?:\/\d+)?$/im)?.[1];
  if (!opusPayloadType) return description;
  const codecParams = [
    ['maxaveragebitrate', String(audioBitrateKbps * 1000)],
    ['useinbandfec', '1'],
    ['usedtx', audioProcessingMode === 'studio' ? '0' : '1'],
    ...(audioProcessingMode === 'studio'
      ? [
          ['stereo', '1'],
          ['sprop-stereo', '1'],
        ]
      : []),
  ] as const;
  const fmtpPattern = new RegExp(`^a=fmtp:${opusPayloadType} (.+)$`, 'im');
  const existing = description.sdp.match(fmtpPattern);
  if (!existing) {
    return {
      ...description,
      sdp: description.sdp.replace(
        new RegExp(`(^a=rtpmap:${opusPayloadType} opus/48000.*$)`, 'im'),
        `$1\r\na=fmtp:${opusPayloadType} ${codecParams
          .map(([key, value]) => `${key}=${value}`)
          .join(';')}`,
      ),
    };
  }
  const currentFmtp = existing[1] ?? '';
  const params = new Map(
    currentFmtp
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const [key, ...rest] = part.split('=');
        return [key, rest.join('=') || '1'] as const;
      }),
  );
  for (const [key, value] of codecParams) {
    params.set(key, value);
  }
  return {
    ...description,
    sdp: description.sdp.replace(
      fmtpPattern,
      `a=fmtp:${opusPayloadType} ${[...params.entries()]
        .map(([key, value]) => `${key}=${value}`)
        .join(';')}`,
    ),
  };
}

/** Build getUserMedia audio constraints for a channel tier. Fidelity
 *  (sample rate / channels) follows the mode; the noise/echo/gain flags are
 *  layered on from the user's voice prefs so they apply on every channel. */
export function buildAudioConstraints(audioProcessingMode: AudioProcessingMode): MediaTrackConstraints {
  const studio = audioProcessingMode === 'studio';
  const channelLevel: MediaTrackConstraints = {
    sampleRate: { ideal: 48000 },
    channelCount: { ideal: studio ? 2 : 1 },
  };
  return applyUserVoicePrefs(channelLevel);
}

/**
 * True when a getUserMedia failure is an `OverconstrainedError` on `deviceId`
 * — i.e. the saved `{ deviceId: { exact } }` points at a device that's gone
 * (unplugged, id rotated across sessions, permission reset). The caller should
 * retry without the device pin so the user still gets the default device
 * instead of a hard failure. `OverconstrainedError` is its own interface (NOT
 * a DOMException in every browser), so we match on name/constraint defensively.
 */
export function isOverconstrainedDeviceError(err: unknown): boolean {
  const e = err as { name?: unknown; constraint?: unknown } | null;
  const name = typeof e?.name === 'string' ? e.name : '';
  const constraint = typeof e?.constraint === 'string' ? e.constraint : '';
  if (name === 'OverconstrainedError') return true;
  return /overconstrained/i.test(name) || constraint === 'deviceId';
}

/** Drop the `deviceId` pin from audio constraints — the fallback when the
 *  saved device is overconstrained. Pure; returns a new object. */
export function stripDeviceId(constraints: MediaTrackConstraints): MediaTrackConstraints {
  const { deviceId: _deviceId, ...rest } = constraints;
  return rest;
}

/** Resolve a stored `outputDeviceId` to a `setSinkId` argument: the empty
 *  string routes to the OS default (both AudioContext.setSinkId and
 *  HTMLMediaElement.setSinkId treat '' as "system default"). */
export function resolveOutputSink(outputDeviceId: string): string {
  return outputDeviceId && outputDeviceId !== 'default' ? outputDeviceId : '';
}

/** Collapse a getUserMedia/getDisplayMedia failure into a stable, kind-
 *  scoped error code the UI can map to a friendly message. */
export function normalizeMediaError(
  err: unknown,
  kind: 'microphone' | 'camera' | 'screen',
): Error {
  const raw =
    err instanceof DOMException
      ? err.name
      : err instanceof Error
        ? err.message
        : String(err ?? '');
  if (/NotAllowedError|Permission denied|denied/i.test(raw)) {
    return new Error(`${kind}_permission_denied`);
  }
  // A device pinned by id that no longer exists surfaces as either
  // NotFoundError or OverconstrainedError depending on the browser — both mean
  // "the requested device is gone", so map them to the same not-found code.
  if (/NotFoundError|OverconstrainedError|Requested device not found|device/i.test(raw)) {
    return new Error(`${kind}_not_found`);
  }
  if (/NotReadableError|TrackStartError|Could not start/i.test(raw)) {
    return new Error(`${kind}_busy`);
  }
  return new Error(`${kind}_access_failed`);
}
